import React, { useState, useEffect } from 'react';
import './App.css';
import { User } from './models/User';
import { Task, ExerciseTask } from './models/Task';
import { Buddy } from './models/Buddy';
import { StorageService } from './services/StorageService';
import { NotificationService } from './services/NotificationService';
import { initializeFirebase, getCurrentUser, onAuthStateChange, signInWithGoogle, signOutUser, saveTasks, saveBuddyData, saveUserData, saveAuthProfile, saveGoogleFitSnapshot, getGoogleFitDailyHistory, syncLocalDataToFirebase, syncFirebaseToLocalData } from './services/FirebaseService';
import { GoogleFitService } from './services/GoogleFitService';
import TaskCreator from './components/TaskCreator';
import ExerciseRoutineBuilder from './components/ExerciseRoutineBuilder';
import WorkoutTracker from './components/WorkoutTracker';
import HeaderBar from './components/dashboard/HeaderBar';
import AuthOverlays from './components/dashboard/AuthOverlays';
import ProgressBanner from './components/dashboard/ProgressBanner';
import BuddyPanel from './components/dashboard/BuddyPanel';
import GoogleFitPanel from './components/dashboard/GoogleFitPanel';
import AchievementsPanel from './components/dashboard/AchievementsPanel';
import TasksPanel from './components/dashboard/TasksPanel';
import QuickActions from './components/dashboard/QuickActions';
import SettingsModal from './components/dashboard/SettingsModal';
import DevPanel from './components/dashboard/DevPanel';
import FooterBar from './components/dashboard/FooterBar';
import BackToTopButton from './components/dashboard/BackToTopButton';
import DashboardShell from './components/dashboard/DashboardShell';
import { migrateLegacyAchievements, applyAchievementEvent, getAchievementCards } from './services/AchievementService';
import packageJson from '../package.json';

function App() {
  const verboseFirebaseLogs = process.env.REACT_APP_VERBOSE_FIREBASE_LOGS === 'true';
  const logFirebase = (...args) => {
    if (verboseFirebaseLogs) {
      console.log(...args);
    }
  };

  const [user, setUser] = useState(null);
  const [buddy, setBuddy] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [authUser, setAuthUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showSyncNotification, setShowSyncNotification] = useState(false);
  const [outOfSyncData, setOutOfSyncData] = useState(null);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [showEvolution, setShowEvolution] = useState(false);
  const [showTaskCreator, setShowTaskCreator] = useState(false);
  const [showRoutineBuilder, setShowRoutineBuilder] = useState(false);
  const [showWorkoutTracker, setShowWorkoutTracker] = useState(false);
  const [fitConnected, setFitConnected] = useState(false);
  const [fitLoading, setFitLoading] = useState(false);
  const [fitSteps, setFitSteps] = useState(null);
  const [fitHeartRate, setFitHeartRate] = useState(null);
  const [fitRestingHeartRate, setFitRestingHeartRate] = useState(null);
  const [fitDailyHistory, setFitDailyHistory] = useState(() => StorageService.getGoogleFitDaily());
  const [fitLastSyncedAt, setFitLastSyncedAt] = useState(() => StorageService.getGoogleFitLatest()?.syncedAt || null);
  const [fitError, setFitError] = useState('');
  const [fitCollapsed, setFitCollapsed] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [progressExpanded, setProgressExpanded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Buddy selection flow states
  const [showAnimalSelection, setShowAnimalSelection] = useState(false);
  const [showEggSelector, setShowEggSelector] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  // DEV mode states
  const [devMode, setDevMode] = useState(() => localStorage.getItem('dev_mode') === 'true');
  const [showDevPanel, setShowDevPanel] = useState(false);
  const [devUnlocked, setDevUnlocked] = useState(() => {
    return localStorage.getItem('dev_unlocked') === 'true' || localStorage.getItem('dev_mode') === 'true';
  });
  const [devUnlockClicks, setDevUnlockClicks] = useState(0);
  const [devUnlockTimer, setDevUnlockTimer] = useState(null);
  const [collapsedStat, setCollapsedStat] = useState(() => {
    return localStorage.getItem('collapsed_stat') || 'streak';
  });
  const [enableBackToTop, setEnableBackToTop] = useState(() => {
    return localStorage.getItem('enable_back_to_top') !== 'false';
  });
  const [reduceMotion, setReduceMotion] = useState(() => {
    return localStorage.getItem('reduce_motion') === 'true';
  });
  const [colorScheme, setColorScheme] = useState(() => {
    return localStorage.getItem('color_scheme') || 'default';
  });
  const [autoRotateStats, setAutoRotateStats] = useState(() => {
    return localStorage.getItem('auto_rotate_stats') === 'true';
  });
  const [rotatingStat, setRotatingStat] = useState('streak');
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-reduce-motion', reduceMotion ? 'true' : 'false');
    localStorage.setItem('reduce_motion', reduceMotion ? 'true' : 'false');
  }, [reduceMotion]);

  useEffect(() => {
    document.documentElement.setAttribute('data-color-scheme', colorScheme);
    localStorage.setItem('color_scheme', colorScheme);
  }, [colorScheme]);

  useEffect(() => {
    localStorage.setItem('collapsed_stat', collapsedStat);
  }, [collapsedStat]);

  useEffect(() => {
    localStorage.setItem('enable_back_to_top', enableBackToTop ? 'true' : 'false');
  }, [enableBackToTop]);

  useEffect(() => {
    localStorage.setItem('auto_rotate_stats', autoRotateStats ? 'true' : 'false');
  }, [autoRotateStats]);

  useEffect(() => {
    if (!autoRotateStats) {
      setRotatingStat(collapsedStat);
      return;
    }

    const order = ['streak', 'xp', 'level'];
    let index = Math.max(0, order.indexOf(rotatingStat));
    const interval = setInterval(() => {
      index = (index + 1) % order.length;
      setRotatingStat(order[index]);
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRotateStats, rotatingStat, collapsedStat]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const hydrateTasks = (rawTasks = []) => {
    if (!Array.isArray(rawTasks)) return [];
    return rawTasks.map(task => task.type === 'exercise' ? new ExerciseTask(task) : new Task(task));
  };

  const ensureUser = (rawUser) => {
    let userData = rawUser || StorageService.getUser();
    if (!userData) {
      userData = new User({ name: 'Traveler' });
    }
    const migratedUser = migrateLegacyAchievements(userData);
    const userInstance = new User(migratedUser);
    userInstance.updateStreak();
    StorageService.saveUser(userInstance);
    return userInstance;
  };

  const ensureBuddy = (rawBuddy) => {
    let buddyData = rawBuddy || StorageService.getBuddy();
    if (!buddyData) {
      buddyData = new Buddy({ name: 'Dulce' });
    }
    const buddyInstance = new Buddy(buddyData);
    buddyInstance.decay();
    StorageService.saveBuddy(buddyInstance);
    return buddyInstance;
  };

  const getDateKey = (value = new Date()) => {
    if (typeof value === 'string') {
      return value.slice(0, 10);
    }
    return value.toISOString().slice(0, 10);
  };

  const saveGoogleFitLocalSnapshot = ({ latest, history }) => {
    if (latest) {
      const normalizedSyncedAt = latest.syncedAt?.toDate
        ? latest.syncedAt.toDate().toISOString()
        : (typeof latest.syncedAt === 'string' ? latest.syncedAt : null);
      const normalizedLatest = {
        ...latest,
        syncedAt: normalizedSyncedAt || new Date().toISOString(),
      };

      StorageService.saveGoogleFitLatest(normalizedLatest);
      setFitLastSyncedAt(normalizedLatest.syncedAt);
      setFitSteps(Number.isFinite(normalizedLatest.steps) ? normalizedLatest.steps : null);
      setFitHeartRate(Number.isFinite(normalizedLatest.heartRateAvg) ? normalizedLatest.heartRateAvg : null);
      setFitRestingHeartRate(Number.isFinite(normalizedLatest.restingHeartRateAvg) ? normalizedLatest.restingHeartRateAvg : null);
    }

    if (Array.isArray(history)) {
      StorageService.saveGoogleFitDaily(history);
      setFitDailyHistory(history);
    }
  };

  const getSuggestedStepGoal = (history = fitDailyHistory) => {
    if (!Array.isArray(history) || history.length === 0) {
      return 8000;
    }

    const recent = history.slice(0, 14).filter(item => Number.isFinite(item.steps) && item.steps > 0);
    if (!recent.length) {
      return 8000;
    }

    const avgSteps = recent.reduce((sum, item) => sum + item.steps, 0) / recent.length;
    const goal = Math.round((avgSteps * 1.05) / 500) * 500;
    return Math.max(4000, Math.min(20000, goal));
  };

  const initializeState = ({ userData, tasksData, buddyData, exerciseHistoryData, googleFitLatest, googleFitDaily } = {}) => {
    const userInstance = ensureUser(userData);
    const buddyInstance = ensureBuddy(buddyData);
    const hydratedTasks = hydrateTasks(tasksData ?? StorageService.getTasks());

    setUser(userInstance);
    setBuddy(buddyInstance);
    setTasks(hydratedTasks);
    
    StorageService.saveTasks(hydratedTasks);
    if (exerciseHistoryData) {
      StorageService.saveExerciseHistory(exerciseHistoryData);
    }
    saveGoogleFitLocalSnapshot({
      latest: googleFitLatest || StorageService.getGoogleFitLatest(),
      history: googleFitDaily || StorageService.getGoogleFitDaily()
    });
    
    // Check if first-time user needs to select buddy
    if (authUser && !userInstance.hasSelectedBuddy) {
      setShowAnimalSelection(true);
    }
    
    NotificationService.requestPermission();
  };

  const getLocalSnapshot = () => ({
    user: StorageService.getUser(),
    tasks: StorageService.getTasks(),
    buddy: StorageService.getBuddy(),
    workoutLogs: {},
    exerciseHistory: StorageService.getExerciseHistory(),
    googleFitLatest: StorageService.getGoogleFitLatest(),
  });

  const clearLocalWorkoutData = () => {
    localStorage.removeItem('workout_log');
    localStorage.removeItem('workout_routines');
    localStorage.removeItem('current_week');
    localStorage.removeItem('completed_tasks');
  };

  const hasLocalData = (snapshot) => {
    if (!snapshot) return false;
    return Boolean(snapshot.user || (snapshot.tasks && snapshot.tasks.length) || snapshot.buddy);
  };

  const isDataOutOfSync = (localSnapshot, firebaseData) => {
    if (!localSnapshot || !firebaseData) return false;
    
    // Compare key data points
    const localUser = localSnapshot.user;
    const cloudUser = firebaseData.user;
    
    // Check if user data differs (level, XP, etc.)
    if (localUser && cloudUser) {
      if (localUser.level !== cloudUser.level || 
          localUser.xp !== cloudUser.xp ||
          localUser.streakDays !== cloudUser.streakDays) {
        return true;
      }
    }
    
    // Check if tasks differ
    const localTasksCount = localSnapshot.tasks?.length || 0;
    const cloudTasksCount = firebaseData.tasks?.length || 0;
    if (localTasksCount !== cloudTasksCount) {
      return true;
    }
    
    // Check if buddy differs
    if (localSnapshot.buddy && firebaseData.buddy) {
      if (localSnapshot.buddy.level !== firebaseData.buddy.level ||
          localSnapshot.buddy.eggColor !== firebaseData.buddy.eggColor) {
        return true;
      }
    }
    
    return false;
  };

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setFitError('Add REACT_APP_GOOGLE_CLIENT_ID to enable Google Fit.');
      return;
    }
    GoogleFitService.init(clientId).catch(err => {
      setFitError(err.message || 'Google Fit initialization failed.');
    });
  }, []);

  // Initialize app
  useEffect(() => {
    let unsubscribe;
    const initializeApp = async () => {
      try {
        // Initialize Firebase
        await initializeFirebase();
        unsubscribe = onAuthStateChange(async (firebaseUser) => {
          setAuthReady(true);
          setAuthUser(firebaseUser || null);

          if (firebaseUser) {
            saveAuthProfile(firebaseUser).catch(err => console.warn('Failed to sync auth profile to Firestore:', err));
            const lastUid = localStorage.getItem('dulce_last_uid');
            if (lastUid && lastUid !== firebaseUser.uid) {
              StorageService.clearAll();
              clearLocalWorkoutData();
              localStorage.removeItem('dulce_has_logged_in');
            }
            localStorage.setItem('dulce_last_uid', firebaseUser.uid);
            setShowLogin(false);
            const hasLoggedInBefore = localStorage.getItem('dulce_has_logged_in') === 'true';
            try {
              // Fetch Firebase data (source of truth)
              let firebaseData = await syncFirebaseToLocalData(firebaseUser.uid).catch(() => null);
              let firebaseFitHistory = await getGoogleFitDailyHistory(firebaseUser.uid, 60).catch(() => []);
              const localSnapshot = getLocalSnapshot();

              //  FIRST-TIME USER: Auto-sync local data to Firebase
              if (!hasLoggedInBefore && hasLocalData(localSnapshot)) {
                logFirebase(' First-time login detected with local progress. Auto-syncing to Firebase...');
                try {
                  await syncLocalDataToFirebase(firebaseUser.uid, localSnapshot);
                  logFirebase(' Local data synced to Firebase');
                } catch (syncError) {
                  console.warn(' Failed to sync local data to Firebase:', syncError);
                  // Continue with local data if sync fails
                }
                initializeState(localSnapshot);
                localStorage.setItem('dulce_has_logged_in', 'true');
                return;
              }

              //  RETURNING USER: Use Firebase as source of truth
              if (firebaseData) {
                // Check if local data exists and is out of sync
                if (hasLoggedInBefore && hasLocalData(localSnapshot) && isDataOutOfSync(localSnapshot, firebaseData)) {
                  logFirebase(' Local data is out of sync with Firebase. Showing sync notification.');
                  setOutOfSyncData({ 
                    local: localSnapshot, 
                    cloud: firebaseData,
                    userId: firebaseUser.uid 
                  });
                  setShowSyncNotification(true);
                }
                
                // Always use Firebase data as source of truth
                if (firebaseData.exerciseHistory) {
                  StorageService.saveExerciseHistory(firebaseData.exerciseHistory);
                }
                initializeState({
                  userData: firebaseData.user,
                  tasksData: firebaseData.tasks,
                  buddyData: firebaseData.buddy,
                  exerciseHistoryData: firebaseData.exerciseHistory,
                  googleFitLatest: firebaseData.googleFitLatest,
                  googleFitDaily: firebaseFitHistory
                });
              } else {
                // No Firebase data - initialize app and sync to Firebase
                logFirebase(' No Firebase data found. Initializing new user data.');
                initializeState();
                const newSnapshot = getLocalSnapshot();
                try {
                  await syncLocalDataToFirebase(firebaseUser.uid, newSnapshot);
                  logFirebase(' New user data synced to Firebase');
                } catch (syncError) {
                  console.warn(' Failed to sync new user data to Firebase:', syncError);
                }
              }
              localStorage.setItem('dulce_has_logged_in', 'true');
            } catch (error) {
              console.warn('Firebase sync failed, using local storage:', error);
              initializeState();
            }
          } else {
            setShowLogin(true);
            initializeState();
          }
        });
      } catch (error) {
        console.warn('Firebase initialization failed, using local storage:', error);
        setAuthReady(true);
        setShowLogin(true);
        initializeState();
      }
    };

    initializeApp();

    // Check for recurring tasks daily
    const recurringInterval = setInterval(() => {
      const now = new Date();
      const today = now.toDateString();

      const tasksData = StorageService.getTasks() || [];
      let updated = false;

      tasksData.forEach(task => {
        if (task.recurring && task.completed) {
          const completedDate = new Date(task.completedAt).toDateString();
          const shouldReset =
            (task.recurring === 'daily' && completedDate !== today) ||
            (task.recurring === 'weekly' &&
             (now - new Date(task.completedAt)) / (1000 * 60 * 60 * 24) >= 7);

          if (shouldReset) {
            task.completed = false;
            task.completedAt = null;
            task.lastSatisfiedAt = null;
            updated = true;
            NotificationService.showNotification(
              ' Task Reset!',
              `${task.title} is ready again!`
            );
          }
        }
      });

      if (updated) {
        const hydratedTasks = hydrateTasks(tasksData);
        StorageService.saveTasks(hydratedTasks);
        setTasks(hydratedTasks);
      }
    }, 60000); // Check every minute

    return () => {
      clearInterval(recurringInterval);
      if (unsubscribe) {
        unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const evaluateGoogleFitAchievements = (currentUser, history, latestFit) => {
    if (!currentUser || !Array.isArray(history) || !latestFit) {
      return { updatedUser: currentUser, unlocked: [] };
    }

    const achievementSet = new Set(Array.isArray(currentUser.achievements) ? currentUser.achievements : []);
    const unlocked = [];
    const unlock = (label) => {
      if (!achievementSet.has(label)) {
        achievementSet.add(label);
        unlocked.push(label);
      }
    };

    const sortedByDate = [...history].sort((a, b) => (b.dateKey || '').localeCompare(a.dateKey || ''));
    const todaySteps = Number.isFinite(latestFit.steps) ? latestFit.steps : 0;

    if (todaySteps >= 5000) unlock(' First 5k Steps');
    if (todaySteps >= 10000) unlock(' 10k Steps Champion');
    if (todaySteps >= 15000) unlock(' 15k Steps Elite');

    const activeLast7 = sortedByDate
      .slice(0, 7)
      .filter(item => Number.isFinite(item.steps) && item.steps >= 6000)
      .length;
    if (activeLast7 >= 5) {
      unlock(' Active 5/7 Week');
    }

    let streakCount = 0;
    for (const day of sortedByDate) {
      if (Number.isFinite(day.steps) && day.steps >= 8000) {
        streakCount += 1;
      } else {
        break;
      }
    }
    if (streakCount >= 3) unlock(' 3-Day Step Streak');
    if (streakCount >= 7) unlock(' 7-Day Step Streak');

    const restingSamples = sortedByDate
      .map(item => Number.isFinite(item.restingHeartRateAvg) ? item.restingHeartRateAvg : null)
      .filter(value => Number.isFinite(value) && value > 0);

    if (restingSamples.length >= 56) {
      const current28 = restingSamples.slice(0, 28);
      const previous28 = restingSamples.slice(28, 56);
      const currentAvg = current28.reduce((sum, value) => sum + value, 0) / current28.length;
      const previousAvg = previous28.reduce((sum, value) => sum + value, 0) / previous28.length;
      if (currentAvg < previousAvg) {
        unlock(' Resting HR Improving (28d)');
      }
    }

    if (!unlocked.length) {
      return { updatedUser: currentUser, unlocked: [] };
    }

    const updatedUser = new User({
      ...currentUser,
      achievements: Array.from(achievementSet)
    });
    return { updatedUser, unlocked };
  };

  const autoCompleteGoogleFitTasks = (fitSnapshot, history) => {
    if (!fitSnapshot || !user || !buddy || !Array.isArray(tasks) || !tasks.length) {
      return;
    }

    const dateKey = fitSnapshot.dateKey || getDateKey();
    const updatedTasks = tasks.map(task => task.type === 'exercise' ? new ExerciseTask(task) : new Task(task));
    let totalXp = 0;
    let hasTaskUpdates = false;
    const completedTitles = [];

    updatedTasks.forEach(task => {
      if (task.completed || task.goalSource !== 'google-fit' || !task.autoComplete) {
        return;
      }

      if (!Number.isFinite(task.targetValue) || task.targetValue <= 0) {
        return;
      }

      const alreadySatisfiedToday = task.lastSatisfiedAt && getDateKey(task.lastSatisfiedAt) === dateKey;
      if (alreadySatisfiedToday) {
        return;
      }

      const metricValue = task.metricType === 'heartRate'
        ? fitSnapshot.heartRateAvg
        : fitSnapshot.steps;

      if (!Number.isFinite(metricValue)) {
        return;
      }

      if (metricValue >= task.targetValue) {
        task.lastSatisfiedAt = new Date().toISOString();
        totalXp += task.complete();
        completedTitles.push(task.title);
        hasTaskUpdates = true;
      }
    });

    let updatedUser = new User({ ...user });
    let updatedBuddy = new Buddy({ ...buddy });
    let changed = hasTaskUpdates;

    if (totalXp > 0) {
      updatedUser.addXP(totalXp);
      const leveledUp = updatedUser.checkLevelUp();
      if (leveledUp) {
        setShowLevelUp(true);
        const evolved = updatedBuddy.evolve(updatedUser.level);
        if (evolved) {
          setShowEvolution(true);
        }
        setTimeout(() => setShowLevelUp(false), 3000);
        setTimeout(() => setShowEvolution(false), 3000);
      }
      updatedBuddy.interact();
      NotificationService.showNotification(' Google Fit goals completed', `Completed ${completedTitles.length} task(s) from synced metrics.`);
      changed = true;
    }

    const { updatedUser: achievementUser, unlocked } = evaluateGoogleFitAchievements(updatedUser, history, fitSnapshot);
    if (unlocked.length) {
      updatedUser = achievementUser;
      unlocked.forEach((label) => {
        NotificationService.showNotification(' New achievement', label);
      });
      changed = true;
    }

    if (!changed) {
      return;
    }

    if (hasTaskUpdates) {
      StorageService.saveTasks(updatedTasks);
      setTasks(hydrateTasks(updatedTasks));
    }
    StorageService.saveUser(updatedUser);
    StorageService.saveBuddy(updatedBuddy);
    setUser(new User(updatedUser));
    setBuddy(new Buddy(updatedBuddy));

    const firebaseUser = getCurrentUser();
    if (firebaseUser) {
      if (hasTaskUpdates) {
        saveTasks(firebaseUser.uid, updatedTasks).catch(err => console.warn('Failed to sync fit-completed tasks:', err));
      }
      saveUserData(firebaseUser.uid, updatedUser).catch(err => console.warn('Failed to sync fit achievements/user data:', err));
      saveBuddyData(firebaseUser.uid, updatedBuddy).catch(err => console.warn('Failed to sync fit buddy updates:', err));
    }
  };

  const completeTask = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.completed) return;

    const xpEarned = task.complete();
    user.addXP(xpEarned);
    
    // Check for level up
    const leveledUp = user.checkLevelUp();
    if (leveledUp) {
      setShowLevelUp(true);
      const evolved = buddy.evolve(user.level);
      if (evolved) {
        setShowEvolution(true);
      }
      setTimeout(() => setShowLevelUp(false), 3000);
      setTimeout(() => setShowEvolution(false), 3000);
    }

    // Buddy interaction
    buddy.interact();

    const achievementResult = applyAchievementEvent(user, {
      type: 'task_completed',
      count: 1,
      level: user.level,
    });
    const updatedUserWithAchievements = new User(achievementResult.userData);
    achievementResult.unlocked.forEach((achievement) => {
      NotificationService.showNotification(' New achievement', achievement.title);
    });

    // Save state to local storage
    StorageService.saveUser(updatedUserWithAchievements);
    StorageService.saveBuddy(buddy);
    StorageService.saveTasks(tasks);

    // Sync to Firestore
    const firebaseUser = getCurrentUser();
    if (firebaseUser) {
      saveTasks(firebaseUser.uid, tasks).catch(err => console.warn('Failed to sync tasks to Firestore:', err));
      saveBuddyData(firebaseUser.uid, buddy).catch(err => console.warn('Failed to sync buddy to Firestore:', err));
      saveUserData(firebaseUser.uid, updatedUserWithAchievements).catch(err => console.warn('Failed to sync user to Firestore:', err));
    }

    // Update UI - create new instances to trigger re-render
    setUser(updatedUserWithAchievements);
    setBuddy(new Buddy(buddy));
    setTasks(hydrateTasks(tasks));
  };

  const addTask = (taskData) => {
    const newTask = taskData.type === 'exercise' 
      ? new ExerciseTask(taskData) 
      : new Task(taskData);
    
    const updatedTasks = [...tasks, newTask];
    setTasks(updatedTasks);
    StorageService.saveTasks(updatedTasks);
    
    // Sync to Firestore
    const firebaseUser = getCurrentUser();
    if (firebaseUser) {
      saveTasks(firebaseUser.uid, updatedTasks).catch(err => console.warn('Failed to sync tasks to Firestore:', err));
      saveUserData(firebaseUser.uid, user).catch(err => console.warn('Failed to sync user to Firestore:', err));

    }
  };

  const deleteTask = (taskId) => {
    const updatedTasks = tasks.filter(task => task.id !== taskId);
    setTasks(updatedTasks);
    StorageService.saveTasks(updatedTasks);

    const firebaseUser = getCurrentUser();
    if (firebaseUser) {
      saveTasks(firebaseUser.uid, updatedTasks).catch(err => console.warn('Failed to sync tasks to Firestore:', err));
      saveUserData(firebaseUser.uid, user).catch(err => console.warn('Failed to sync user to Firestore:', err));
    }
  };

  const completeRoutineTasks = (routine) => {
    if (!routine?.name) return;
    const routinePrefix = `${routine.name} - `;
    let totalXp = 0;
    let completedAny = false;
    let completedTaskCount = 0;

    tasks.forEach(task => {
      if (task.completed) return;
      if (task.type !== 'exercise') return;
      if (!task.title.startsWith(routinePrefix)) return;
      totalXp += task.complete();
      completedAny = true;
      completedTaskCount += 1;
    });

    if (!completedAny) return;

    user.addXP(totalXp);

    const leveledUp = user.checkLevelUp();
    if (leveledUp) {
      setShowLevelUp(true);
      const evolved = buddy.evolve(user.level);
      if (evolved) {
        setShowEvolution(true);
      }
      setTimeout(() => setShowLevelUp(false), 3000);
      setTimeout(() => setShowEvolution(false), 3000);
    }

    buddy.interact();

    const taskAchievementResult = applyAchievementEvent(user, {
      type: 'task_completed',
      count: completedTaskCount,
      level: user.level,
    });
    const workoutAchievementResult = applyAchievementEvent(taskAchievementResult.userData, {
      type: 'workout_logged',
      count: 1,
      level: user.level,
    });
    const updatedUserWithAchievements = new User(workoutAchievementResult.userData);
    [...taskAchievementResult.unlocked, ...workoutAchievementResult.unlocked].forEach((achievement) => {
      NotificationService.showNotification(' New achievement', achievement.title);
    });

    StorageService.saveUser(updatedUserWithAchievements);
    StorageService.saveBuddy(buddy);
    StorageService.saveTasks(tasks);

    const firebaseUser = getCurrentUser();
    if (firebaseUser) {
      saveTasks(firebaseUser.uid, tasks).catch(err => console.warn('Failed to sync tasks to Firestore:', err));
      saveBuddyData(firebaseUser.uid, buddy).catch(err => console.warn('Failed to sync buddy to Firestore:', err));
      saveUserData(firebaseUser.uid, updatedUserWithAchievements).catch(err => console.warn('Failed to sync user to Firestore:', err));
    }

    setUser(updatedUserWithAchievements);
    setBuddy(new Buddy(buddy));
    setTasks(hydrateTasks(tasks));
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.warn('Login failed:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOutUser();
      StorageService.clearAll();
      clearLocalWorkoutData();
      localStorage.removeItem('dulce_has_logged_in');
      localStorage.removeItem('dulce_last_uid');
      const defaultBuddy = new Buddy({ name: 'Dulce' });
      setBuddy(defaultBuddy);
      StorageService.saveBuddy(defaultBuddy);
      setFitConnected(false);
      setFitSteps(null);
      setFitHeartRate(null);
      setFitRestingHeartRate(null);
      setFitDailyHistory([]);
      setFitLastSyncedAt(null);
    } catch (error) {
      console.warn('Logout failed:', error);
    }
  };

  const handleMergeConfirm = async () => {
    if (!outOfSyncData) return;
    try {
      logFirebase(' Syncing local data to Firebase...');
      await syncLocalDataToFirebase(outOfSyncData.userId, outOfSyncData.local);
      logFirebase(' Local data synced to Firebase');
      initializeState(outOfSyncData.local);
      setShowSyncNotification(false);
      setOutOfSyncData(null);
    } catch (error) {
      console.warn(' Failed to sync local data to Firebase:', error);
      alert('Failed to sync data. Please try again.');
    }
  };

  const handleTogglePaidRetention = () => {
    if (!user) return;

    const currentlyPaid = user.subscriptionTier === 'paid' || user.fitUnlimitedHistory === true || user.isPaidSubscriber === true;
    const nextPaid = !currentlyPaid;

    const updatedUser = new User({
      ...user,
      subscriptionTier: nextPaid ? 'paid' : 'free',
      fitHistoryRetention: nextPaid ? 'unlimited' : '12m',
      fitUnlimitedHistory: nextPaid,
      isPaidSubscriber: nextPaid,
    });

    setUser(updatedUser);
    StorageService.saveUser(updatedUser);

    const firebaseUser = getCurrentUser();
    if (firebaseUser) {
      saveUserData(firebaseUser.uid, updatedUser).catch(err => console.warn('Failed to sync subscription mode to Firestore:', err));
    }
  };
  
  // Buddy selection handlers
  const handleSelectAnimal = (animalKey) => {
    setSelectedAnimal(animalKey);
    setShowAnimalSelection(false);
    setShowEggSelector(true);
  };
  
  const handleBackToAnimalSelection = () => {
    setShowEggSelector(false);
    setShowAnimalSelection(true);
  };
  
  const handleConfirmBuddy = async (buddyConfig) => {
    const newBuddy = new Buddy({
      ...buddy,
      animalType: buddyConfig.animalType,
      eggColor: buddyConfig.eggColor,
      rarity: buddyConfig.rarity,
      isRandomRoll: buddyConfig.isRandomRoll,
      level: 1,
      stage: 'egg'
    });
    
    const updatedUser = new User({
      ...user,
      hasSelectedBuddy: true,
      buddyPreferences: {
        favoriteAnimal: buddyConfig.animalType,
        selectedEggColor: buddyConfig.eggColor
      }
    });
    
    setBuddy(newBuddy);
    setUser(updatedUser);
    StorageService.saveBuddy(newBuddy);
    StorageService.saveUser(updatedUser);
    
    // Sync to Firestore
    const firebaseUser = getCurrentUser();
    if (firebaseUser) {
      try {
        await saveBuddyData(firebaseUser.uid, newBuddy);
        await saveUserData(firebaseUser.uid, updatedUser);
      } catch (error) {
        console.warn('Failed to sync buddy selection to Firestore:', error);
      }
    }
    
    setShowEggSelector(false);
    setSelectedAnimal(null);
  };
  
  const handleRerollBuddy = async () => {
    const rolledResult = Buddy.rollRandomBuddy();
    
    let newBuddyConfig;
    if (rolledResult) {
      // User got a special roll
      newBuddyConfig = rolledResult;
    } else {
      // No special roll - keep current animal but reset to level 1
      newBuddyConfig = {
        animalType: buddy.animalType,
        eggColor: buddy.eggColor,
        rarity: buddy.rarity,
        isRandomRoll: false
      };
    }
    
    const newBuddy = new Buddy({
      name: buddy.name,
      animalType: newBuddyConfig.animalType,
      eggColor: newBuddyConfig.eggColor,
      rarity: newBuddyConfig.rarity,
      isRandomRoll: newBuddyConfig.isRandomRoll,
      level: 1,
      stage: 'egg',
      happiness: 100
    });
    
    setBuddy(newBuddy);
    StorageService.saveBuddy(newBuddy);
    
    // Sync to Firestore
    const firebaseUser = getCurrentUser();
    if (firebaseUser) {
      try {
        await saveBuddyData(firebaseUser.uid, newBuddy);
      } catch (error) {
        console.warn('Failed to sync rerolled buddy to Firestore:', error);
      }
    }
    
    // Show notification
    if (rolledResult) {
      const rarityMsg = rolledResult.rarity === 'mythical' ? ' MYTHICAL' : 
                       rolledResult.rarity === 'rare' ? ' RARE' : '';
      alert(` You got a ${rarityMsg} buddy! Welcome your new ${newBuddy.getAnimalConfig().name}!`);
    } else {
      alert('No special roll this time, but your journey continues with renewed energy! ');
    }
  };

  const handleRerollBuddyRequest = () => {
    if (window.confirm(
      ` This will reset your buddy to Level 1 and try for a new random animal!\n\nYour current: Level ${buddy?.level} ${buddy?.getAnimalConfig().name}\n\nContinue?`
    )) {
      handleRerollBuddy();
    }
  };

  const handleToggleDevMode = () => {
    const nextMode = !devMode;
    setDevMode(nextMode);
    localStorage.setItem('dev_mode', String(nextMode));
    if (!nextMode) {
      setShowDevPanel(false);
    }
  };

  const handleDevUnlockClick = () => {
    if (devUnlockTimer) {
      clearTimeout(devUnlockTimer);
    }

    const nextClicks = devUnlockClicks + 1;
    setDevUnlockClicks(nextClicks);

    if (nextClicks >= 5) {
      setDevUnlocked(true);
      localStorage.setItem('dev_unlocked', 'true');
      setDevUnlockClicks(0);
      return;
    }

    const timer = setTimeout(() => {
      setDevUnlockClicks(0);
    }, 2000);
    setDevUnlockTimer(timer);
  };

  const devSetLevel = (level) => {
    const newBuddy = new Buddy({ ...buddy, level });
    newBuddy.evolve(level);
    setBuddy(newBuddy);
    StorageService.saveBuddy(newBuddy);
  };

  const devSetStage = (stage) => {
    const newBuddy = new Buddy({ ...buddy, stage });
    setBuddy(newBuddy);
    StorageService.saveBuddy(newBuddy);
  };

  const devSetAnimal = (animalType, rarity) => {
    const newBuddy = new Buddy({
      ...buddy,
      animalType,
      rarity,
      isRandomRoll: false
    });
    setBuddy(newBuddy);
    StorageService.saveBuddy(newBuddy);
  };

  const devSetEggColor = (eggColor) => {
    const newBuddy = new Buddy({ ...buddy, eggColor });
    setBuddy(newBuddy);
    StorageService.saveBuddy(newBuddy);
  };

  const devSetHappiness = (happiness) => {
    const newBuddy = new Buddy({
      ...buddy,
      happiness,
      lastInteraction: new Date().toISOString(),
    });
    setBuddy(newBuddy);
    StorageService.saveBuddy(newBuddy);
  };

  const devAddXP = (amount) => {
    const newUser = new User({ ...user });
    newUser.addXP(amount);
    setUser(newUser);
    StorageService.saveUser(newUser);

    const newBuddy = new Buddy({ ...buddy });
    const evolved = newBuddy.evolve(newUser.level);
    setBuddy(newBuddy);
    StorageService.saveBuddy(newBuddy);
    if (evolved) {
      setShowEvolution(true);
      setTimeout(() => setShowEvolution(false), 3000);
    }
  };

  const devResetFirstTimeSelection = () => {
    const newUser = new User({ ...user, hasSelectedBuddy: false });
    setUser(newUser);
    StorageService.saveUser(newUser);
    setShowAnimalSelection(true);
    setShowEggSelector(false);
  };

  const refreshGoogleFit = async () => {
    const steps = await GoogleFitService.getStepsToday();
    const heart = await GoogleFitService.getHeartRateData();

    const snapshot = {
      dateKey: getDateKey(),
      steps: Number.isFinite(steps.steps) ? steps.steps : 0,
      heartRateAvg: Number.isFinite(heart.heartRate) ? heart.heartRate : 0,
      restingHeartRateAvg: Number.isFinite(heart.restingHeartRate) ? heart.restingHeartRate : 0,
      source: 'google-fit',
      syncedAt: new Date().toISOString(),
    };

    setFitConnected(true);
    setFitSteps(snapshot.steps);
    setFitHeartRate(snapshot.heartRateAvg);
    setFitRestingHeartRate(snapshot.restingHeartRateAvg);
    setFitLastSyncedAt(snapshot.syncedAt);

    const localHistory = StorageService.getGoogleFitDaily();
    const mergedLocalHistoryMap = new Map(localHistory.map(item => [item.dateKey, item]));
    mergedLocalHistoryMap.set(snapshot.dateKey, {
      ...(mergedLocalHistoryMap.get(snapshot.dateKey) || {}),
      ...snapshot,
    });
    let activeHistory = Array.from(mergedLocalHistoryMap.values())
      .sort((a, b) => (b.dateKey || '').localeCompare(a.dateKey || ''));

    const firebaseUser = getCurrentUser();
    if (firebaseUser) {
      await saveGoogleFitSnapshot(firebaseUser.uid, snapshot).catch(err => {
        console.warn('Failed to sync Google Fit snapshot to Firestore:', err);
      });

      const remoteHistory = await getGoogleFitDailyHistory(firebaseUser.uid, 90).catch(() => []);
      if (remoteHistory.length) {
        activeHistory = remoteHistory;
      }
    }

    saveGoogleFitLocalSnapshot({ latest: snapshot, history: activeHistory });
    autoCompleteGoogleFitTasks(snapshot, activeHistory);
  };

  const handleGoogleFitConnect = async () => {
    setFitLoading(true);
    setFitError('');
    try {
      await GoogleFitService.connect();
      setFitConnected(true);
      await refreshGoogleFit();
    } catch (error) {
      setFitError(error.message || 'Google Fit connection failed.');
    } finally {
      setFitLoading(false);
    }
  };

  const handleGoogleFitRefresh = async () => {
    setFitLoading(true);
    setFitError('');
    try {
      await refreshGoogleFit();
    } catch (error) {
      setFitError(error.message || 'Google Fit refresh failed.');
    } finally {
      setFitLoading(false);
    }
  };

  const getEmailHandle = (email) => {
    if (!email || typeof email !== 'string') return '';
    const handle = email.split('@')[0] || '';
    if (!handle) return '';
    return handle.charAt(0).toUpperCase() + handle.slice(1);
  };

  const emailHandle = getEmailHandle(authUser?.email);
  const displayName = authUser?.displayName || '';
  const profileName = displayName || emailHandle || 'Fitness';
  const titleName = displayName || emailHandle;
  const possessiveName = titleName
    ? titleName.endsWith('s') || titleName.endsWith('S')
      ? `${titleName}'`
      : `${titleName}'s`
    : '';
  const appTitle = possessiveName ? `${possessiveName} Fitness Journey` : 'Fitness Journey';
  const buildId = process.env.REACT_APP_BUILD_ID || 'local';
  const suggestedStepGoal = getSuggestedStepGoal();
  const buddyHappiness = Math.max(0, Math.min(100, Number(buddy?.happiness) || 0));
  const buddyMood = buddy?.getMood?.() || 'neutral';
  const buddyMoodLabel = buddyMood.charAt(0).toUpperCase() + buddyMood.slice(1);
  const paidRetentionEnabled = user?.subscriptionTier === 'paid' || user?.fitUnlimitedHistory === true || user?.isPaidSubscriber === true;
  const achievementCards = getAchievementCards(user);

  const authOverlays = (
    <AuthOverlays
      showLogin={showLogin}
      showSyncNotification={showSyncNotification}
      showAnimalSelection={showAnimalSelection}
      showEggSelector={showEggSelector}
      selectedAnimal={selectedAnimal}
      authUser={authUser}
      onGoogleLogin={handleGoogleLogin}
      onMergeConfirm={handleMergeConfirm}
      onDismissSync={() => {
        setShowSyncNotification(false);
        setOutOfSyncData(null);
      }}
      onSelectAnimal={handleSelectAnimal}
      onConfirmBuddy={handleConfirmBuddy}
      onBackToAnimalSelection={handleBackToAnimalSelection}
    />
  );

  const resolvedCollapsedStat = () => {
    const activeStat = autoRotateStats ? rotatingStat : collapsedStat;
    if (activeStat === 'streak' && user?.streakDays <= 0) return 'xp';
    return activeStat;
  };

  const renderCollapsedProgress = () => {
    const statKey = resolvedCollapsedStat();
    if (statKey === 'level') {
      return (
        <>
          <span className="progress-label">Level</span>
          <span className="progress-value">{user.level}</span>
        </>
      );
    }

    if (statKey === 'xp') {
      const nextXp = user.getXPForNextLevel();
      const progress = Math.min(100, (user.xp / nextXp) * 100);
      return (
        <>
          <span className="progress-label">XP</span>
          <span className="progress-value">{user.xp} / {nextXp}</span>
          <span className="progress-mini">
            <span className="progress-mini-fill" style={{ width: `${progress}%` }} />
          </span>
        </>
      );
    }

    return (
      <>
        <span className="progress-label">Streak</span>
        <span className="progress-value"> {user.streakDays} days</span>
      </>
    );
  };

  useEffect(() => {
    if (!authUser || !buddy || !profileName) return;
    if (buddy.name === profileName) return;

    const updatedBuddy = new Buddy({ ...buddy, name: profileName });
    setBuddy(updatedBuddy);
    StorageService.saveBuddy(updatedBuddy);

    const firebaseUser = getCurrentUser();
    if (firebaseUser) {
      saveBuddyData(firebaseUser.uid, updatedBuddy).catch(err => console.warn('Failed to sync buddy:', err));
    }
  }, [authUser, buddy, profileName]);

  if (!authReady) {
    return <div className="loading">Loading your journey...</div>;
  }

  if (!user || !buddy) {
    return (
      <div className="loading">
        Loading your journey...
        {authOverlays}
      </div>
    );
  }

  const settingsModalNode = (
    <SettingsModal
      isOpen={showSettings}
      onClose={() => setShowSettings(false)}
      authUser={authUser}
      paidRetentionEnabled={paidRetentionEnabled}
      onTogglePaidRetention={handleTogglePaidRetention}
      collapsedStat={collapsedStat}
      onSetCollapsedStat={setCollapsedStat}
      autoRotateStats={autoRotateStats}
      onToggleAutoRotate={() => setAutoRotateStats(prev => !prev)}
      buddy={buddy}
      onRerollBuddy={handleRerollBuddyRequest}
      theme={theme}
      onSetTheme={setTheme}
      colorScheme={colorScheme}
      onSetColorScheme={setColorScheme}
      reduceMotion={reduceMotion}
      onToggleReduceMotion={() => setReduceMotion(prev => !prev)}
      enableBackToTop={enableBackToTop}
      onToggleBackToTop={() => setEnableBackToTop(prev => !prev)}
      devUnlocked={devUnlocked}
      devMode={devMode}
      onDevUnlockClick={handleDevUnlockClick}
      onToggleDevMode={handleToggleDevMode}
      onOpenDevPanel={() => setShowDevPanel(true)}
      version={packageJson.version}
      buildId={buildId}
    />
  );

  const tasksNode = (
    <>
      <TasksPanel
        tasks={tasks}
        onCompleteTask={completeTask}
        onDeleteTask={deleteTask}
        onOpenWorkoutTracker={() => setShowWorkoutTracker(true)}
      />

      {showTaskCreator && (
        <TaskCreator
          suggestedStepGoal={suggestedStepGoal}
          onAddTask={(taskData) => {
            addTask(taskData);
            setShowTaskCreator(false);
          }}
          onClose={() => setShowTaskCreator(false)}
        />
      )}

      {showRoutineBuilder && (
        <ExerciseRoutineBuilder
          onCreateRoutine={addTask}
          onClose={() => setShowRoutineBuilder(false)}
        />
      )}

      {showWorkoutTracker && (
        <WorkoutTracker
          onClose={() => setShowWorkoutTracker(false)}
          authUserId={authUser?.uid || null}
          onFinishRoutine={completeRoutineTasks}
        />
      )}

      {tasks.filter(t => t.completed).length > 0 && (
        <div className="completed-section">
          <h3> Completed</h3>
          <div className="completed-list">
            {tasks.filter(t => t.completed).map(task => (
              <div key={task.id} className="completed-item">
                {task.title}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const overlaysNode = (
    <>
      {authOverlays}
      {showLevelUp && (
        <div className="notification level-up">
           Level Up! You're now level {user.level}!
        </div>
      )}
      {showEvolution && (
        <div className="notification evolution">
           {buddy.name} evolved into {buddy.stage}! {buddy.getEmoji()}
        </div>
      )}
      <DevPanel
        isOpen={devMode && showDevPanel}
        onClose={() => setShowDevPanel(false)}
        authUser={authUser}
        buddy={buddy}
        onSetLevel={devSetLevel}
        onSetStage={devSetStage}
        onSetAnimal={devSetAnimal}
        onSetEggColor={devSetEggColor}
        onSetHappiness={devSetHappiness}
        onAddXP={devAddXP}
        onResetFirstTimeSelection={devResetFirstTimeSelection}
        onShowAnimalSelector={() => setShowAnimalSelection(true)}
        onShowEggSelector={() => {
          setSelectedAnimal('bird');
          setShowEggSelector(true);
        }}
      />
    </>
  );

  return (
    <div className="App">
      <DashboardShell
        header={(
          <HeaderBar
            profileName={profileName}
            authUser={authUser}
            theme={theme}
            appTitle={appTitle}
            onToggleTheme={toggleTheme}
            onOpenSettings={() => setShowSettings(true)}
            onLogin={handleGoogleLogin}
            onLogout={handleLogout}
          />
        )}
        overlays={overlaysNode}
        settingsModal={settingsModalNode}
        progress={(
          <ProgressBanner
            user={user}
            collapsedStat={collapsedStat}
            autoRotateStats={autoRotateStats}
            progressExpanded={progressExpanded}
            renderCollapsedProgress={renderCollapsedProgress}
            onOpen={() => setProgressExpanded(true)}
            onClose={() => setProgressExpanded(false)}
            onSetCollapsedStat={setCollapsedStat}
            onToggleAutoRotate={setAutoRotateStats}
          />
        )}
        buddy={(
          <BuddyPanel
            buddy={buddy}
            buddyMood={buddyMood}
            buddyMoodLabel={buddyMoodLabel}
            buddyHappiness={buddyHappiness}
          />
        )}
        fit={(
          <GoogleFitPanel
            fitCollapsed={fitCollapsed}
            fitConnected={fitConnected}
            fitLoading={fitLoading}
            fitError={fitError}
            fitSteps={fitSteps}
            fitHeartRate={fitHeartRate}
            fitRestingHeartRate={fitRestingHeartRate}
            fitLastSyncedAt={fitLastSyncedAt}
            suggestedStepGoal={suggestedStepGoal}
            onToggleCollapsed={() => setFitCollapsed(prev => !prev)}
            onConnect={handleGoogleFitConnect}
            onRefresh={handleGoogleFitRefresh}
          />
        )}
        achievements={<AchievementsPanel achievements={user.achievements} cards={achievementCards} />}
        tasks={tasksNode}
        quickActions={(
          <QuickActions
            onOpenTaskCreator={() => setShowTaskCreator(true)}
            onOpenRoutineBuilder={() => setShowRoutineBuilder(true)}
            onOpenWorkoutTracker={() => setShowWorkoutTracker(true)}
          />
        )}
        footer={<FooterBar version={packageJson.version} buildId={buildId} />}
        backToTop={(
          <BackToTopButton
            showBackToTop={showBackToTop}
            enableBackToTop={enableBackToTop}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          />
        )}
      />
    </div>
  );
}

export default App;
